import Edit from '@mui/icons-material/Edit'
import { Button } from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useCallback, useEffect } from 'react'
import { useHistory } from 'react-router'
import {
	BaseFormPackageProcessorInstanceModel,
	ConfigurableFormPackageProcessor,
	FormPackageProcessorMetadata,
} from '../../../../../../../../api/DTOtemp'
import { FormPackageVersionClient } from '../../../../../../../../api/clients/identity'
import { FormPackageProcessorInstancesClient } from '../../../../../../../../api/clients/identity/FormPackageProcessorInstancesClient'
import { FormPackageProcessorsClient } from '../../../../../../../../api/clients/identity/FormPackageProcessorsClient'
import { FullscreenSpinner } from '../../../../../../../../components/feedback/circular'
import { useModals } from '../../../../../../../../services/notifications/ModalService'
import { toastService } from '../../../../../../../../services/notifications/ToastService'
import AdministrationPageContainer from '../../../../../AdministrationPageContainer'
import { FormPackageAdministrationPageProps } from '../../FormPackagePage'
import { useFormPackageContext } from '../../FormPackagePageContext'
import { AddFormPackageProcessorDialog } from './AddFormPackageProcessorDialog'
import { FormPackageProcessorsTable } from './FormPackageProcessorsTable'

type FormPackageProcessorsAdministrationPageProps = {
	packageId: number
	packageVersionNumber: number
}

export const FormPackageProcessorsAdministrationPage = observer(
	(
		props: FormPackageAdministrationPageProps &
			FormPackageProcessorsAdministrationPageProps,
	) => {
		const formPackageContext = useFormPackageContext()

		const modalService = useModals()
		const history = useHistory()

		const localStore = useLocalObservable(() => ({
			processorInstances:
				formPackageContext.formPackage.configuration.processors
					.slice()
					.sort((a, b) => a.index - b.index),
			processors: [] as ConfigurableFormPackageProcessor[],
			isLoading: false,
		}))

		useEffect(() => {
			const processorsClient = new FormPackageProcessorsClient()

			runInAction(() => {
				localStore.isLoading = true
			})

			processorsClient.getFormPackageProcessors().then(
				action((response) => {
					localStore.processors = response.data

					localStore.isLoading = false
				}),
			)
		}, [])

		const handleDeleteProcessor = useCallback(
			async (id: number) => {
				const api = new FormPackageProcessorInstancesClient(props.packageId)

				try {
					await api.deleteProcessorInstance(id)

					runInAction(() => {
						localStore.processorInstances =
							localStore.processorInstances.filter((v) => v.id !== id)
					})

					toastService.displayToast({
						message: 'Processor deleted',
						area: 'global',
						severity: 'success',
					})
				} catch (e) {
					console.error(e)

					toastService.displayToast({
						message: 'Error deleting processor',
						area: 'global',
						severity: 'error',
					})
				}
			},
			[props.packageId, props.packageVersionNumber],
		)

		return (
			<AdministrationPageContainer
				title="Form Package Processors"
				actions={
					<div>
						{localStore.processors.length > 0 && (
							<Button
								variant="contained"
								startIcon={<Edit />}
								onClick={() => {
									modalService
										.showForm((modalProps) => (
											<AddFormPackageProcessorDialog
												onConfirm={(instance) =>
													modalProps.close({
														closeResult: 'okay',
														value: instance,
													})
												}
												onCancel={() =>
													modalProps.close({ closeResult: 'cancel' })
												}
												packageId={props.packageId}
												processors={localStore.processors}
											/>
										))
										.then((response) => {
											if (response.closeResult === 'cancel') return

											const instance =
												response.value as BaseFormPackageProcessorInstanceModel

											const processorInstancesClient =
												new FormPackageProcessorInstancesClient(props.packageId)

											processorInstancesClient
												.createFormPackageProcessorInstance(instance)
												.then((response) => {
													toastService.displayToast({
														message: 'Form package processor created',
														area: 'global',
														severity: 'success',
													})
													history.push(
														`${props.packageId}/_processors/${response.data.id}`,
													)
												})
												.catch((e) => {
													console.error(e)

													toastService.displayToast({
														message: 'Error creating form package processor',
														area: 'global',
														severity: 'error',
													})
												})
										})
								}}
							>
								Add Processor
							</Button>
						)}
					</div>
				}
				tabs={props.tabLabels}
				currentTab={11}
				onTabChanged={props.onTabChanged}
				hideAttachmentsTab={props.hideAttachmentsTab}
			>
				<ProcessorsSection
					packageId={props.packageId}
					packageVersionNumber={props.packageVersionNumber}
					processorInstances={localStore.processorInstances}
					processors={localStore.processors}
					onDeleteProcessor={handleDeleteProcessor}
					isLoading={localStore.isLoading}
				/>
			</AdministrationPageContainer>
		)
	},
)

type ProcessorsSectionProps = {
	processorInstances: FormPackageProcessorMetadata[]
	processors: ConfigurableFormPackageProcessor[]
	isLoading: boolean
	onDeleteProcessor: (id: number) => void
}

const ProcessorsSection = observer(
	(
		props: ProcessorsSectionProps &
			FormPackageProcessorsAdministrationPageProps,
	) => {
		const localStore = useLocalObservable(() => ({
			processorsPositionsDirty: false,
		}))

		const formPackageContext = useFormPackageContext()

		const handleProcessorPositionsChanged = useCallback(
			action(() => {
				localStore.processorsPositionsDirty = true
			}),
			[localStore],
		)

		const handleOrderSaved = useCallback(
			action((processors: FormPackageProcessorMetadata[]) => {
				const api = new FormPackageVersionClient(props.packageId)

				const updatedConfiguration =
					formPackageContext.formPackage.configuration
				updatedConfiguration.processors = processors

				api.UpdateConfiguration({
					...formPackageContext.formPackage,
					configuration: updatedConfiguration,
				})

				localStore.processorsPositionsDirty = false
			}),
			[props.packageId, props.packageVersionNumber],
		)

		if (props.isLoading) return <FullscreenSpinner />

		return (
			<FormPackageProcessorsTable
				processorInstances={props.processorInstances}
				processors={props.processors}
				processorOrderDirty={localStore.processorsPositionsDirty}
				onDeleteProcessor={props.onDeleteProcessor}
				onMoveProcessors={handleProcessorPositionsChanged}
				onSaveProcessorPositions={handleOrderSaved}
			/>
		)
	},
)
